import classNames from "classnames";
import {
  inputFieldVariants,
  wrapperVariants,
} from "components/DesignSystem/Input/BareInput";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { PlusIcon } from "components/icons/PlusIcon";
import dayjs from "dayjs";
import { FieldProps, useFormikContext } from "formik";
import { ComponentProps, forwardRef, HTMLAttributes, Ref } from "react";
import ReactDatePicker, { DatePickerProps } from "react-datepicker";
import { createPortal } from "react-dom";

const CustomInput = forwardRef(
  (
    {
      onClick,
      value,
      ...props
    }: {
      onClick: () => void;
      value: string;
    } & HTMLAttributes<HTMLButtonElement>,
    ref: Ref<HTMLButtonElement>
  ) => (
    <button {...props} className="all:unset" onClick={onClick} ref={ref}>
      {value || <PlusIcon />}
    </button>
  )
);

const CustomInputText = forwardRef(
  (props: ComponentProps<"input">, ref: Ref<HTMLInputElement>) => (
    <input
      ref={ref}
      {...props}
      id={props.name}
      className={classNames(
        wrapperVariants({ size: "regular", variant: "default" }),
        inputFieldVariants({ size: "regular" })
      )}
    />
  )
);

export const BareTimePicker = ({
  name,
  label,
  value,
  onDateChange,
  ...props
}: Omit<
  DatePickerProps,
  "onChange" | "selectsMultiple" | "selectsRange" | "showMonthYearDropdown"
> & {
  onDateChange: (value: string | null) => void;
  label?: string;
}) => {
  return (
    <>
      {createPortal(
        <div
          className="t-pointer-events-auto"
          id={`Time-picker-portal-${name}`}
        />,
        document.body
      )}
      <ReactDatePicker
        {...props}
        id={name}
        selectsRange={undefined}
        selectsMultiple={undefined}
        // @ts-ignore
        customInput={!label ? <CustomInput /> : <CustomInputText />}
        showTimeSelect
        wrapperClassName="t-block"
        popperClassName="t-z-dropdown"
        portalId={`Time-picker-portal-${name}`}
        timeIntervals={15}
        timeCaption="Time"
        dateFormat="HH:mm"
        timeFormat="HH:mm"
        placeholderText="HH:MM"
        {...(value && typeof value === "string"
          ? {
              selected: dayjs(value).toDate(),
            }
          : {})}
        showTimeSelectOnly={true}
        value={undefined}
        onChange={(
          date: Date | null,
          event?:
            | React.MouseEvent<HTMLElement>
            | React.KeyboardEvent<HTMLElement>
        ) => {
          const actualDate = Array.isArray(date) ? date[0] : date;
          const savingDate = actualDate
            ? dayjs(actualDate).toISOString()
            : null;
          onDateChange(savingDate);
        }}
      />
    </>
  );
};

export const TimePicker = ({
  name,
  label,
  ...props
}: FieldProps["field"] & { label?: string; disabled?: boolean }) => {
  const { values, setFieldValue } = useFormikContext<Record<string, string>>();

  return (
    <div>
      {createPortal(
        <div
          className="t-pointer-events-auto"
          id={`Time-picker-portal-${name}`}
        />,
        document.body
      )}
      {label && <Label htmlFor={name}>{label}</Label>}
      <ReactDatePicker
        {...props}
        // @ts-ignore
        customInput={!label ? <CustomInput /> : <CustomInputText />}
        showTimeSelect
        wrapperClassName="t-block"
        popperClassName="t-z-dropdown"
        portalId={`Time-picker-portal-${name}`}
        timeIntervals={15}
        timeCaption="Time"
        dateFormat="HH:mm"
        timeFormat="HH:mm"
        placeholderText="HH:MM"
        {...(values[name]
          ? {
              selected: dayjs(values[name]).toDate(),
            }
          : {})}
        showTimeSelectOnly={true}
        value={undefined}
        onChange={(date: Date | Date[] | null) => {
          const actualDate = Array.isArray(date) ? date[0] : date;
          const savingDate = actualDate
            ? dayjs(actualDate).toISOString()
            : null;
          setFieldValue(name, savingDate);
        }}
      />
    </div>
  );
};
